// vg-binding: P7.D-04
// vg-binding: P7.D-05
//
// apps/vendor/src/features/dashboard/MiniProductionQueue.tsx
//
// Top-5 urgent orders block with SLA countdown chip + click-to-detail
// nav. Color chip mirrors the BE `color_tier` enum so the FE doesn't
// re-derive thresholds.

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { cn } from '@printway/ui';

import type { ProductionQueueRow } from './api.js';

interface Props {
  rows: ProductionQueueRow[];
  isLoading?: boolean;
}

const CHIP: Record<ProductionQueueRow['color_tier'], string> = {
  green: 'bg-green-100 text-green-800',
  yellow: 'bg-yellow-100 text-yellow-800',
  orange: 'bg-orange-100 text-orange-800',
  red: 'bg-red-100 text-red-800',
};

function formatDeadline(ms: number, overdueLabel: string): string {
  const diffH = Math.round((ms - Date.now()) / (60 * 60 * 1000));
  if (diffH <= 0) return overdueLabel;
  if (diffH < 24) return `${diffH}h`;
  return `${Math.round(diffH / 24)}d`;
}

function SkeletonRow(): JSX.Element {
  return (
    <li className="h-14 animate-pulse rounded-md border border-pw-border bg-pw-surface" />
  );
}

export function MiniProductionQueue({ rows, isLoading }: Props): JSX.Element {
  const { t } = useTranslation('dashboard');
  return (
    <section className="rounded-xl border border-pw-border bg-pw-bg p-5 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-pw-text">{t('queue.title')}</h3>
        <Link
          to="/orders"
          className="text-xs font-medium text-pw-primary hover:underline"
        >
          {t('queue.viewAll')}
        </Link>
      </div>

      {isLoading ? (
        <ul className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_v, i) => (
            <SkeletonRow key={i} />
          ))}
        </ul>
      ) : rows.length === 0 ? (
        <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-6 text-center text-sm text-pw-text-muted">
          {t('queue.empty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((r) => (
            <li key={r.item_id}>
              <Link
                to={`/orders/${r.order_id}`}
                data-testid={`dashboard-queue-row-${r.item_id}`}
                className="flex items-center justify-between gap-3 rounded-md border border-pw-border bg-pw-surface px-3 py-2 transition-colors hover:bg-pw-bg"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-pw-text">
                    {r.product}
                  </p>
                  <p className="truncate text-xs text-pw-text-muted">
                    {t('queue.rowMeta', { orderId: r.order_id, qty: r.qty })}
                  </p>
                </div>
                <span
                  className={cn(
                    'inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold',
                    CHIP[r.color_tier],
                  )}
                >
                  {t('queue.slaChip', {
                    percent: r.sla_percent_used,
                    deadline: formatDeadline(r.sla_deadline_ms, t('queue.overdue')),
                  })}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default MiniProductionQueue;
